/**
 * PATH: packages/email-hub/src/feature/components/EmailSearchSuggestions.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-EMAILSEARCHSUGGESTIONS-001
 * PURPOSE: In-context suggestion popover for the inbox search input.
 *          Shows Gmail-style operators (`from:`, `has:`, `is:`,
 *          `before:`, `after:`) with one-line examples + descriptions.
 *          Click any row to insert/replace into the search input;
 *          Arrow / Enter / Escape keyboard navigation handled by
 *          the parent (InboxPage owns the input + key handler).
 *
 * WHY:     Operator request 2026-05-19 — operator typed a name in
 *          the search box and the inbox didn't surface example
 *          searches like `from:`. The BE already supports the
 *          filtering; the FE just needed to learn Gmail's
 *          vocabulary and surface it discoverably.
 *
 * LANDMINE (2026-07-31): Must PORTAL to document.body with fixed
 *          coords. An in-flow `absolute z-30` panel loses to the
 *          inbox list's sticky mailbox headers (`backdrop-blur-sm`
 *          stacking context) — suggestions painted under/through
 *          rows (same family as useAnchoredMenu / Layout #7).
 */

import { Search } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../_internal/lib/utils'
import type { SearchSuggestion } from '../lib/parseEmailSearchQuery'

const MAX_SUGGESTIONS = 8

export interface EmailSearchSuggestionsProps {
  /** Output of `suggestEmailSearchTokens`. */
  suggestions: SearchSuggestion[]
  /** Which row is highlighted (driven by the parent's Arrow handler). */
  highlightedIndex: number
  /** Called when the user clicks a suggestion. */
  onSelect: (suggestion: SearchSuggestion) => void
  /** Called when the mouse enters a row, so the parent can update
   *  the highlighted index in sync with mouse + keyboard. */
  onHoverIndex?: (index: number) => void
  /** Anchor (search input wrapper) — panel width/position track this. */
  anchorRef: RefObject<HTMLElement | null>
  /** Dismiss when Escape / outside click (parent also owns blur). */
  onDismiss?: () => void
}

interface PanelCoords {
  top: number
  left: number
  width: number
}

export default function EmailSearchSuggestions({
  suggestions,
  highlightedIndex,
  onSelect,
  onHoverIndex,
  anchorRef,
  onDismiss,
}: EmailSearchSuggestionsProps) {
  const [coords, setCoords] = useState<PanelCoords | null>(null)

  const recompute = useCallback(() => {
    const el = anchorRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setCoords({
      top: r.bottom + 4,
      left: Math.max(8, r.left),
      width: Math.max(200, r.width),
    })
  }, [anchorRef])

  useLayoutEffect(() => {
    recompute()
  }, [recompute, suggestions.length])

  useEffect(() => {
    recompute()
    const onResize = () => recompute()
    const onScroll = () => recompute()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss?.()
    }
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (anchorRef.current?.contains(target)) return
      const panel = document.getElementById('email-search-suggestions-portal')
      if (panel?.contains(target)) return
      onDismiss?.()
    }
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll, true)
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onDown)
    }
  }, [anchorRef, onDismiss, recompute])

  if (suggestions.length === 0 || !coords) return null
  const visible = suggestions.slice(0, MAX_SUGGESTIONS)
  const overflowCount = suggestions.length - visible.length

  const panel = (
    <div
      id="email-search-suggestions-portal"
      role="listbox"
      aria-label="Search suggestions"
      data-testid="email-search-suggestions"
      style={{
        top: coords.top,
        left: coords.left,
        width: coords.width,
      }}
      className={cn(
        // Portal + fixed escapes sticky/backdrop-blur stacking under
        // the inbox list. Opaque solid fill (not translucent popover
        // tokens) so row text never bleeds through.
        'fixed z-[9999]',
        'rounded-md border border-border',
        'bg-white text-neutral-900 dark:bg-zinc-900 dark:text-zinc-100',
        'shadow-xl overflow-hidden',
        'animate-in fade-in-0 zoom-in-95 duration-100',
      )}
    >
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border bg-neutral-50 dark:bg-zinc-800/80 flex items-center gap-1.5">
        <Search className="h-3 w-3" aria-hidden />
        <span>Try these searches</span>
      </div>
      <ul className="py-1 max-h-72 overflow-y-auto bg-white dark:bg-zinc-900">
        {visible.map((s, idx) => (
          <li key={`${s.insertText}-${idx}`} role="option" aria-selected={idx === highlightedIndex}>
            <button
              type="button"
              // Use onMouseDown so the click fires BEFORE the input's
              // blur event would tear the popover down. (Click would
              // miss; mousedown lands first.)
              onMouseDown={(e) => {
                e.preventDefault()
                onSelect(s)
              }}
              onMouseEnter={() => onHoverIndex?.(idx)}
              className={cn(
                'w-full text-left px-3 py-1.5 text-xs',
                'transition-colors',
                idx === highlightedIndex
                  ? 'bg-accent text-accent-foreground'
                  : 'hover:bg-accent/60',
              )}
            >
              <div className="flex items-baseline gap-2">
                <code className={cn(
                  'font-mono font-medium',
                  idx === highlightedIndex ? 'text-primary' : 'text-foreground',
                )}>
                  {s.label}
                </code>
                {s.description && (
                  <span className="text-[10px] text-muted-foreground truncate">
                    {s.description}
                  </span>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>
      {overflowCount > 0 && (
        <div className="px-3 py-1 text-[10px] text-muted-foreground border-t border-border bg-neutral-50 dark:bg-zinc-800/80">
          +{overflowCount} more - keep typing to narrow.
        </div>
      )}
      <div className="px-3 py-1 text-[10px] text-muted-foreground border-t border-border bg-neutral-50 dark:bg-zinc-800/80 flex items-center gap-3">
        <span>
          <kbd className="px-1 py-0.5 rounded border border-border bg-background text-[9px]">↑↓</kbd>{' '}
          navigate
        </span>
        <span>
          <kbd className="px-1 py-0.5 rounded border border-border bg-background text-[9px]">⏎</kbd>{' '}
          insert
        </span>
        <span>
          <kbd className="px-1 py-0.5 rounded border border-border bg-background text-[9px]">esc</kbd>{' '}
          dismiss
        </span>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return null
  return createPortal(panel, document.body)
}
